import React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";

import IconLabel from "../UI/IconLabel";
import LayerControl from "./LayerControl";
import Project from "./Project";
import ProjectContents from "./ProjectContents";

import { styles } from "../../config/config.theme";
import { projectStatusCategories } from "../../config/config.projects";
import classes from "./LayerControlGroup.module.css";
import { parseProjectData } from "../../utils";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      color: styles["text-secondary"],
      backgroundColor: "#ddd",
    },
  },
};

function LayerControlGroup({ name, icon, activeLayerGroups }) {
  const layerGroup = useSelector((state) => state.layerGroup?.[name]);
  const projects = useSelector((state) => state.data.projects.data);
  const projectBasins = parseProjectData(projects || []);

  const [projectStatus, setProjectStatus] = useState(projectStatusCategories);

  const [showLayerGroup, setShowLayerGroup] = useState(
    layerGroup?.openByDefault
  );
  // automatically open the layerControlGroup if there's only one active group
  useEffect(() => {
    if (activeLayerGroups.length === 1) {
      setShowLayerGroup(true);
    }
  }, [activeLayerGroups]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setProjectStatus(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const projectCount = Object.keys(projectBasins).includes(name)
    ? projectBasins[name].length
    : 0;

  return (
    <div className={classes["layer-control-group-container"]}>
      <div
        onClick={() => setShowLayerGroup((prevState) => !prevState)}
        className={classes["layer-control-group-header"]}
      >
        <IconLabel className={icon} />
        <h4 className={classes["layer-control-group-name"]}>{name}</h4>
      </div>

      <div
        style={{ width: "100%", display: showLayerGroup ? "block" : "none" }}
      >
        <ProjectContents
          projectInfo={layerGroup?.layerGroupInfo}
          showLayerControl={true}
          style={{
            backgroundColor: styles["background-secondary"],
            color: styles["text-primary"],
            linkSecondaryColor: "#bbb",
          }}
        />

        <Divider />

        {/* Project filter dropdown*/}
        {projectCount > 0 && (
          <React.Fragment>
            <div className={classes["project-filter-container"]}>
              <FormControl
                sx={{
                  m: 1,
                  width: 240,
                  color: styles["text-primary"],
                  "& .Mui-focused": {
                    color: styles["text-primary"],
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                  },
                  "& :focus": {
                    color: styles["text-primary"],
                  },
                }}
                size="small"
              >
                <InputLabel
                  id="demo-multiple-checkbox-label"
                  sx={{ color: styles["text-primary"] }}
                >
                  Filter by Project Status
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={projectStatus}
                  onChange={handleChange}
                  input={<OutlinedInput label="Filter by Project Status" />}
                  renderValue={(selected) => selected.join(", ")}
                  MenuProps={MenuProps}
                  sx={{
                    fontSize: "0.8rem",
                    color: styles["text-primary"],
                    "& .MuiTypography": { fontSize: "0.8rem" },
                  }}
                >
                  {projectStatusCategories.map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                      dense
                      sx={{
                        p: 0,
                      }}
                    >
                      <Checkbox
                        checked={projectStatus.indexOf(status) > -1}
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: styles["accent-color"],
                          },
                        }}
                      />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Projects  */}
            {Object.keys(projectBasins).includes(name)
              ? projectBasins[name]
                  // filter by project status
                  ?.filter((project) =>
                    // if project doesn't have a status property then always include it
                    project.hasOwnProperty("activity_status")
                      ? projectStatus.includes(project.activity_status?.name)
                      : true
                  )
                  // sort alphabetically by project name
                  .sort((a, b) =>
                    a.name > b.name ? 1 : a.name < b.name ? -1 : 0
                  )
                  .map((project, index) => (
                    <Project
                      basinName={name}
                      project={project}
                      id={index}
                      key={project.id || index}
                    />
                  ))
              : null}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default LayerControlGroup;
